import { Calculator, Search } from 'lucide-react';
import { useMemo, useState } from 'react';
import {
  computeTripTableTotals,
  enrichTripExpenses,
  marginTone,
  TRIP_EXPENSES,
} from './mockFinanceData';

function AedCell({ value, bold }) {
  return (
    <span className={`font-mono text-xs ${bold ? 'font-bold text-logi-text' : 'text-logi-muted'}`}>
      {value.toLocaleString()}
    </span>
  );
}

export default function TripExpenseTable({ trips = TRIP_EXPENSES }) {
  const [query, setQuery] = useState('');
  const enriched = useMemo(() => enrichTripExpenses(trips), [trips]);
  const totals = useMemo(() => computeTripTableTotals(trips), [trips]);

  const filtered = useMemo(() => {
    const q = query.toLowerCase();
    return enriched.filter(
      (t) =>
        t.tripId.toLowerCase().includes(q) ||
        t.clientName.toLowerCase().includes(q),
    );
  }, [enriched, query]);

  return (
    <section className="overflow-hidden rounded-xl border border-logi-grid bg-white backdrop-blur-sm">
      <div className="flex flex-wrap items-center justify-between gap-3 border-b border-logi-grid px-4 py-3">
        <div className="flex items-center gap-2">
          <Calculator className="h-4 w-4 text-emerald-700" strokeWidth={1.75} />
          <div>
            <h3 className="text-sm font-bold text-logi-text">Trip Expense Breakdown</h3>
            <p className="text-[11px] text-logi-text0">
              {trips.length} trips · Freight · Fuel · Salik · Allowances · Net margin
            </p>
          </div>
        </div>
        <div className="relative w-full sm:w-52">
          <Search className="pointer-events-none absolute left-2.5 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-logi-text0" />
          <input
            type="search"
            placeholder="Search trip or client…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            className="w-full rounded-lg border border-logi-grid bg-white py-1.5 pl-8 pr-3 text-xs text-logi-text placeholder-logi-subtle outline-none focus:border-emerald-500/50 focus:ring-1 focus:ring-emerald-500/30"
          />
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full min-w-[880px] text-left text-sm">
          <thead>
            <tr className="border-b border-logi-grid/80 bg-white text-[10px] uppercase tracking-wider text-logi-text0">
              <th className="px-4 py-2.5 font-semibold">Trip ID</th>
              <th className="px-4 py-2.5 font-semibold">Client Name</th>
              <th className="px-4 py-2.5 text-right font-semibold">Base Freight (AED)</th>
              <th className="px-4 py-2.5 text-right font-semibold">Fuel (AED)</th>
              <th className="px-4 py-2.5 text-right font-semibold">Tolls &amp; Salik (AED)</th>
              <th className="px-4 py-2.5 text-right font-semibold">Driver Allowance (AED)</th>
              <th className="px-4 py-2.5 text-right font-semibold">Net Margin (%)</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-logi-grid/30">
            {filtered.map((trip) => (
              <tr key={trip.tripId} className="transition-colors hover:bg-logi-silver">
                <td className="px-4 py-2.5">
                  <span className="font-mono text-xs font-bold text-logi-accent">{trip.tripId}</span>
                </td>
                <td className="px-4 py-2.5 text-xs font-medium text-logi-text">{trip.clientName}</td>
                <td className="px-4 py-2.5 text-right">
                  <AedCell value={trip.baseFreight} bold />
                </td>
                <td className="px-4 py-2.5 text-right">
                  <AedCell value={trip.fuelExpense} />
                </td>
                <td className="px-4 py-2.5 text-right">
                  <AedCell value={trip.tollsSalik} />
                </td>
                <td className="px-4 py-2.5 text-right">
                  <AedCell value={trip.driverAllowance} />
                </td>
                <td className="px-4 py-2.5 text-right">
                  <span className={`font-mono text-xs font-bold ${marginTone(trip.netMarginPct)}`}>
                    {trip.netMarginPct}%
                  </span>
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr className="border-t border-logi-grid bg-white text-xs font-semibold">
              <td className="px-4 py-3 text-logi-muted" colSpan={2}>
                Period Total ({filtered.length} trips)
              </td>
              <td className="px-4 py-3 text-right">
                <AedCell value={totals.baseFreight} bold />
              </td>
              <td className="px-4 py-3 text-right">
                <AedCell value={totals.fuelExpense} bold />
              </td>
              <td className="px-4 py-3 text-right">
                <AedCell value={totals.tollsSalik} bold />
              </td>
              <td className="px-4 py-3 text-right">
                <AedCell value={totals.driverAllowance} bold />
              </td>
              <td className="px-4 py-3 text-right">
                <span className={`font-mono font-bold ${marginTone(totals.netMarginPct)}`}>
                  {totals.netMarginPct}%
                </span>
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      {filtered.length === 0 && (
        <p className="px-4 py-8 text-center text-sm text-logi-text0">No trips match your search.</p>
      )}
    </section>
  );
}
